import React from 'react';

const WAVE_HEIGHTS = [22, 35, 48, 30, 55, 40, 62, 38, 50, 28, 45, 58, 33, 52, 42, 60];

export type OnboardingPreviewKey = 'cloud' | 'notes' | 'audio' | 'kanban' | 'comment' | 'promo';

type PreviewSize = 'mobile' | 'desktop' | 'slide';

const sizeClass = (size: PreviewSize) => {
  if (size === 'slide') return 'text-sm';
  return size === 'desktop' ? 'text-[8px]' : 'text-[10px]';
};

export const PreviewTrackTile: React.FC<{
  name: string;
  artist: string;
  status: string;
  sharedViewer?: boolean;
  compact?: boolean;
  large?: boolean;
}> = ({ name, artist, status, sharedViewer = false, compact = false, large = false }) => (
  <div className={`sketch-box text-left w-full bg-[var(--color-surface)] ${large ? 'p-4' : compact ? 'p-2.5' : 'p-3'}`}>
    <div className="flex gap-2 items-start">
      <div
        className={`shrink-0 bg-[var(--color-hover)] rounded-sm flex items-center justify-center opacity-80 ${
          large ? 'w-16 h-16 text-3xl' : compact ? 'w-10 h-10 text-lg' : 'w-12 h-12 text-xl'
        }`}
      >
        🎵
      </div>
      <div className="min-w-0 flex-1">
        <p className={`font-semibold truncate ${large ? 'text-lg' : compact ? 'text-xs' : 'text-sm'}`}>{name}</p>
        <p className={`text-[var(--color-muted)] ${large ? 'text-sm' : compact ? 'text-[9px]' : 'text-[10px]'}`}>{artist}</p>
        <span
          className={`label-typewriter inline-block mt-0.5 px-1.5 py-px border border-dashed border-[var(--color-border)] ${
            large ? 'text-xs mt-1' : compact ? 'text-[7px]' : 'text-[8px]'
          }`}
        >
          {status}
        </span>
        <div className={`bg-[var(--color-hover)] rounded-full overflow-hidden ${large ? 'mt-2 h-1.5' : compact ? 'mt-1 h-1' : 'mt-1.5 h-1'}`}>
          <div className="h-full bg-[var(--brand-from)] w-2/3" />
        </div>
      </div>
      {sharedViewer && (
        <div
          className={`rounded-full flex items-center justify-center shrink-0 ${
            large ? 'w-8 h-8 text-base' : 'w-5 h-5 text-[10px]'
          }`}
          style={{ backgroundColor: 'var(--collab-blue-bg)', color: 'var(--collab-blue-text)' }}
        >
          👁
        </div>
      )}
    </div>
  </div>
);

const OnboardingAppPreviewInner: React.FC<{ variant: OnboardingPreviewKey; size: PreviewSize }> = ({
  variant,
  size,
}) => {
  const compact = size === 'desktop';
  const slide = size === 'slide';

  if (variant === 'cloud') {
    return (
      <div className={`sketch-box bg-[var(--color-surface)] ${slide ? 'p-4 space-y-3' : compact ? 'p-1.5 space-y-1' : 'p-2.5 space-y-1.5'}`}>
        <div
          className={`flex items-center gap-2 bg-[var(--color-hover)] rounded-sm border-[var(--brand-from)] ${
            slide ? 'gap-3 p-3 border-2' : compact ? 'gap-1.5 p-1 border' : 'p-1.5 border-2'
          }`}
        >
          <span className={slide ? 'text-2xl' : compact ? 'text-sm' : 'text-lg'} aria-hidden>
            📁
          </span>
          <div className="min-w-0 flex-1">
            <p className={`font-semibold ${slide ? 'text-base' : compact ? 'text-[8px]' : 'text-[10px]'}`}>Google Drive</p>
            <p className={`text-[var(--color-muted)] ${slide ? 'text-sm' : compact ? 'text-[7px]' : 'text-[9px]'}`}>
              files stay in your account
            </p>
          </div>
          <span className={`label-typewriter text-[var(--brand-from)] ${slide ? 'text-sm' : compact ? 'text-[7px]' : 'text-[8px]'}`}>
            linked
          </span>
        </div>
        <p className={`text-[var(--color-muted)] text-center ${slide ? 'text-sm' : compact ? 'text-[7px]' : 'text-[9px]'}`}>
          or Cassette Cloud
        </p>
      </div>
    );
  }

  if (variant === 'notes') {
    const mp3Size = slide ? 'w-10 h-10 text-[11px]' : compact ? 'w-6 h-6 text-[8px]' : 'w-8 h-8 text-[10px]';
    return (
      <div className={`sketch-box bg-[var(--color-surface)] text-center ${slide ? 'p-4 space-y-3' : compact ? 'p-1.5 space-y-1' : 'p-2.5 space-y-2'}`}>
        <div className={slide ? 'space-y-2' : compact ? 'space-y-0.5' : 'space-y-1'}>
          <p className={`label-typewriter text-[var(--color-muted)] ${slide ? 'text-xs' : compact ? 'text-[7px]' : 'text-[8px]'}`}>
            raw idea
          </p>
          <p className={`border-b border-dashed text-[var(--color-muted)] truncate ${sizeClass(size)}`}>
            midnight train — moody R&B, slow burn
          </p>
          <p className={`border-b border-dashed text-[var(--color-muted)] truncate ${sizeClass(size)}`}>
            hook: softer 2nd line
          </p>
        </div>
        <div
          className={`inline-flex items-center gap-2 border-t border-dashed border-[var(--color-border)] ${
            slide ? 'pt-3 gap-3' : compact ? 'pt-1 gap-1' : 'pt-2 gap-2'
          }`}
        >
          <div
            className={`rounded-full bg-blue-400 flex items-center justify-center text-white font-bold shrink-0 ${mp3Size}`}
            aria-hidden
          >
            MP3
          </div>
          <div className="min-w-0 text-left">
            <p className={`font-medium truncate ${slide ? 'text-sm' : compact ? 'text-[8px]' : 'text-[10px]'}`}>idea-riff.mp3</p>
            <p className={`text-[var(--color-muted)] ${slide ? 'text-xs' : compact ? 'text-[7px]' : 'text-[9px]'}`}>optional demo</p>
          </div>
        </div>
      </div>
    );
  }

  if (variant === 'audio') {
    return (
      <div className={`sketch-box bg-[var(--color-surface)] ${slide ? 'p-4' : compact ? 'p-1.5' : 'p-2'}`}>
        <div className={`flex items-end gap-px mb-1 ${slide ? 'h-14' : compact ? 'h-5' : 'h-10'}`}>
          {WAVE_HEIGHTS.slice(0, slide ? 16 : compact ? 8 : 16).map((h, i) => (
            <div
              key={i}
              className="flex-1 bg-[var(--brand-from)] opacity-60 rounded-t-sm"
              style={{ height: `${h}%` }}
            />
          ))}
        </div>
        <div className={`flex justify-between label-typewriter text-[var(--color-muted)] ${slide ? 'text-xs' : 'text-[7px]'}`}>
          <span>0:00</span>
          <span className="text-[var(--brand-from)]">▶ preview</span>
          <span>3:42</span>
        </div>
      </div>
    );
  }

  if (variant === 'kanban') {
    return (
      <div className={`sketch-box bg-[var(--color-surface)] ${slide ? 'p-4' : compact ? 'p-1.5' : 'p-2'}`}>
        <p className={`label-typewriter text-[var(--color-muted)] ${slide ? 'text-xs mb-2' : compact ? 'text-[7px] mb-0.5' : 'text-[8px] mb-1'}`}>
          Mixing
        </p>
        <div className={`sketch-box bg-[var(--color-hover)] truncate ${slide ? 'p-3 text-base' : compact ? 'p-1 text-[8px]' : 'p-1.5 text-[10px]'}`}>
          Midnight Echoes
        </div>
      </div>
    );
  }

  if (variant === 'comment') {
    return (
      <div className={slide ? 'space-y-2' : compact ? 'space-y-0.5' : 'space-y-1'}>
        <div className={`sketch-box bg-[var(--color-surface)] ${slide ? 'p-3' : compact ? 'p-1' : 'p-2'}`}>
          <div className={`flex items-end gap-px ${slide ? 'h-10' : compact ? 'h-5' : 'h-6'} mb-0.5`}>
            {WAVE_HEIGHTS.slice(0, slide ? 12 : compact ? 8 : 10).map((h, i) => (
              <div
                key={i}
                className="flex-1 bg-[var(--brand-from)] opacity-50 rounded-t-sm"
                style={{ height: `${h}%` }}
              />
            ))}
          </div>
        </div>
        <div className={`sketch-box flex gap-2 items-start bg-[var(--color-surface)] ${slide ? 'p-3 gap-3' : compact ? 'p-1 gap-1' : 'p-2'}`}>
          <span
            className={`sketch-btn sketch-btn-primary font-mono shrink-0 rounded-sm ${
              slide ? 'px-2.5 py-1 text-sm' : compact ? 'px-1 py-px text-[7px]' : 'px-1.5 py-px text-[9px]'
            }`}
          >
            1:24
          </span>
          <p className={`leading-snug truncate ${slide ? 'text-sm' : compact ? 'text-[8px]' : 'text-[10px]'}`}>boost vocals here</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`grid grid-cols-3 ${slide ? 'gap-2' : compact ? 'gap-0.5' : 'gap-1'}`}>
      {['cover', 'promo', 'reel'].map((label) => (
        <div
          key={label}
          className={`sketch-box bg-[var(--color-hover)] flex items-center justify-center label-typewriter text-[var(--color-muted)] ${
            slide ? 'h-16 text-xs' : compact ? 'h-7 text-[6px]' : 'h-10 text-[7px]'
          }`}
        >
          {label}
        </div>
      ))}
    </div>
  );
};

export const OnboardingMiniPreview: React.FC<{ variant: OnboardingPreviewKey; size?: PreviewSize }> = ({
  variant,
  size = 'mobile',
}) => (
  <div className={size === 'desktop' ? 'mt-1.5' : 'mt-3'}>
    <OnboardingAppPreviewInner variant={variant} size={size} />
  </div>
);

/** @deprecated use OnboardingMiniPreview */
export const MiniNotesPreview = OnboardingMiniPreview;
export const MiniAudioPreview = OnboardingMiniPreview;
export const MiniKanbanPreview = OnboardingMiniPreview;
export const MiniCollabPreview = OnboardingMiniPreview;
export const MiniCommentPreview = OnboardingMiniPreview;
export const MiniPromoPreview = OnboardingMiniPreview;
export const MiniTrackBundlePreview: React.FC = () => null;
